import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { CartLine, CartState } from '@/types';
import type { OrderMode } from '@/types';
import { clearTableSession } from '@/features/tableSession/tableSessionSlice';

// Extended local state adds buffet advisory budget tracking on top of the
// persisted CartState. The whole `cart` slice is in the redux-persist whitelist,
// so buffetMaxItemsByRestaurant survives reloads too — harmless: it is advisory
// only and re-seeded from the server's ROUND_BUDGET_EXCEEDED responses.
interface CartSliceState extends CartState {
  /** SC-1243: confirmation belongs to this page load, never to storage. */
  modeConfirmedByRestaurant?: Record<string, boolean>;
  /** Advisory buffet budget per restaurant (max items per round), set from
   *  server ROUND_BUDGET_EXCEEDED error responses. Client gating is UX-only;
   *  server is always authoritative. */
  buffetMaxItemsByRestaurant: Record<string, number>;
}

const initialState: CartSliceState = {
  byRestaurant: {},
  modeByRestaurant: {},
  modeConfirmedByRestaurant: {},
  ui: { isDrawerOpen: false },
  buffetMaxItemsByRestaurant: {},
};

const DEFAULT_MODE: OrderMode = 'takeaway';

interface ByRestoLineAction { restaurantUuid: string; lineId: string; }
interface ByRestoQtyAction extends ByRestoLineAction { qty: number; }
interface ByRestoAddAction { restaurantUuid: string; line: CartLine; }
interface ByRestoClearAction { restaurantUuid: string; }
interface ByRestoUnitPriceAction extends ByRestoLineAction { unitPrice: number; }
interface ByRestoBuffetBudgetAction { restaurantUuid: string; maxItems: number; }

/** Stable key over the merge-relevant fields of a cart line. */
function lineMergeKey(line: CartLine): string {
  const opts = line.selectedOptions
    .map((g) => ({
      groupId: g.groupId,
      options: [...g.options]
        .map((o) => ({ id: o.id, qty: o.qty }))
        .sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0)),
    }))
    .sort((a, b) => a.groupId.localeCompare(b.groupId));
  return JSON.stringify({ itemId: line.itemId, opts, unitPrice: line.unitPrice, note: line.note ?? '' });
}

const cartSlice = createSlice({
  name: 'cart',
  initialState,
  reducers: {
    addLine(state, action: PayloadAction<ByRestoAddAction>) {
      const { restaurantUuid, line } = action.payload;
      if (!state.byRestaurant[restaurantUuid]) state.byRestaurant[restaurantUuid] = [];
      const bucket = state.byRestaurant[restaurantUuid];
      const incomingKey = lineMergeKey(line);
      const existing = bucket.find((l) => lineMergeKey(l) === incomingKey);
      if (existing) {
        existing.qty += line.qty;
      } else {
        bucket.push(line);
      }
    },
    removeLine(state, action: PayloadAction<ByRestoLineAction>) {
      const { restaurantUuid, lineId } = action.payload;
      const bucket = state.byRestaurant[restaurantUuid];
      if (!bucket) return;
      state.byRestaurant[restaurantUuid] = bucket.filter((l) => l.lineId !== lineId);
    },
    updateQty(state, action: PayloadAction<ByRestoQtyAction>) {
      const { restaurantUuid, lineId, qty } = action.payload;
      const bucket = state.byRestaurant[restaurantUuid];
      if (!bucket) return;
      if (qty <= 0) {
        state.byRestaurant[restaurantUuid] = bucket.filter((l) => l.lineId !== lineId);
        return;
      }
      const line = bucket.find((l) => l.lineId === lineId);
      if (line) line.qty = qty;
    },
    clearRestaurantCart(state, action: PayloadAction<ByRestoClearAction>) {
      state.byRestaurant[action.payload.restaurantUuid] = [];
      // SC-1092 : le mode appartient a la commande, pas au client. Une fois le
      // panier vide (commande passee), la suivante repose la question
      // « A emporter ou Livraison ? » avant que le client ne compose son panier.
      if (state.modeByRestaurant) delete state.modeByRestaurant[action.payload.restaurantUuid];
      if (state.modeConfirmedByRestaurant) delete state.modeConfirmedByRestaurant[action.payload.restaurantUuid];
    },
    updateLineUnitPrice(state, action: PayloadAction<ByRestoUnitPriceAction>) {
      const { restaurantUuid, lineId, unitPrice } = action.payload;
      const bucket = state.byRestaurant[restaurantUuid];
      if (!bucket) return;
      const line = bucket.find((l) => l.lineId === lineId);
      if (line) line.unitPrice = unitPrice;
    },
    setMode(state, action: PayloadAction<{ restaurantUuid: string; mode: OrderMode }>) {
      // A v3 persisted blob predates modeByRestaurant; rehydration leaves the
      // map undefined and assignment would throw. Initialize lazily.
      if (!state.modeByRestaurant) state.modeByRestaurant = {};
      state.modeByRestaurant[action.payload.restaurantUuid] = action.payload.mode;
      if (!state.modeConfirmedByRestaurant) state.modeConfirmedByRestaurant = {};
      state.modeConfirmedByRestaurant[action.payload.restaurantUuid] = true;
    },
    /** Advisory buffet budget set from ROUND_BUDGET_EXCEEDED server responses.
     *  maxItems = 0 clears the gate (e.g. after a successful order round). */
    setBuffetBudget(state, action: PayloadAction<ByRestoBuffetBudgetAction>) {
      if (!state.buffetMaxItemsByRestaurant) state.buffetMaxItemsByRestaurant = {};
      const { restaurantUuid, maxItems } = action.payload;
      if (maxItems <= 0) {
        delete state.buffetMaxItemsByRestaurant[restaurantUuid];
      } else {
        state.buffetMaxItemsByRestaurant[restaurantUuid] = maxItems;
      }
    },
    openDrawer(state) { state.ui.isDrawerOpen = true; },
    closeDrawer(state) { state.ui.isDrawerOpen = false; },
  },
  extraReducers: (builder) => {
    // Fix 6c: when the table session is cleared (session end, 401, etc.)
    // also clear the advisory buffet budget so the gate resets.
    builder.addCase(clearTableSession, (state, action) => {
      if (!state.buffetMaxItemsByRestaurant) state.buffetMaxItemsByRestaurant = {};
      delete state.buffetMaxItemsByRestaurant[action.payload];
    });
  },
});

export const {
  addLine,
  removeLine,
  updateQty,
  clearRestaurantCart,
  updateLineUnitPrice,
  setMode,
  setBuffetBudget,
  openDrawer,
  closeDrawer,
} = cartSlice.actions;

export default cartSlice.reducer;

// Selectors (curried by restaurantUuid)
type StateWithCart = { cart: CartSliceState };

/** Reference stable pour un restaurant sans panier. `?? []` rendait un tableau
 *  neuf a chaque appel : `useSelector` le signale comme un resultat instable, et
 *  la carte du serveur (189 lignes abonnees au panier) se re-rendait pour rien. */
const NO_LINES: CartLine[] = [];

export const selectLinesByRestaurant = (uuid: string) => (state: StateWithCart): CartLine[] =>
  state.cart.byRestaurant[uuid] ?? NO_LINES;

export const selectCartCount = (uuid: string) => (state: StateWithCart): number =>
  selectLinesByRestaurant(uuid)(state).reduce((acc, l) => acc + l.qty, 0);

export const selectCartSubtotal = (uuid: string) => (state: StateWithCart): number =>
  selectLinesByRestaurant(uuid)(state).reduce((acc, l) => acc + l.unitPrice * l.qty, 0);

export const selectCartMode = (uuid: string) => (state: StateWithCart): OrderMode =>
  state.cart.modeByRestaurant?.[uuid] ?? DEFAULT_MODE;
/** SC-1243: a restored cart mode is not a fresh answer to the menu chooser. */
export const selectCartModeChosen = (uuid: string) => (state: StateWithCart): boolean =>
  state.cart.modeConfirmedByRestaurant?.[uuid] === true;
export const selectIsDrawerOpen = (state: StateWithCart) => state.cart.ui.isDrawerOpen;

/** Advisory buffet round budget check.
 *  Returns true when the server previously rejected a round with ROUND_BUDGET_EXCEEDED
 *  AND the current cart item count meets or exceeds that maximum.
 *  Client gating is UX-only; server is always authoritative. */
export const selectBuffetBudgetExceeded = (uuid: string) => (state: StateWithCart): boolean => {
  const max = state.cart.buffetMaxItemsByRestaurant?.[uuid];
  if (!max) return false;
  const count = selectCartCount(uuid)(state);
  return count >= max;
};
